import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ControlledTreeEnvironment,
  Tree,
  TreeItem,
  TreeItemIndex,
  DraggingPosition,
  InteractionMode,
} from "react-complex-tree";
import "react-complex-tree/lib/style-modern.css";
import { showDialog } from "@widgets/CommonDialog";
/* ================================
   타입
================================ */

type NodeType =
  | "main"
  | "maindiv"
  | "div"
  | "SearchPanel"
  | "SnGrid"
  | "SnTab"
  | "SnForm"
  | "TabPage"
  | "SnFileUploadBox";

export type DesignerNode = {
  id: string;
  title: string;
  type: NodeType;
  parentId: string | null;
  order: number;
  layout?: "row" | "column"; // main/div 전용
  subType?: "SnGrid" | "SnForm"; // main/div 전용
  subTitle?: string;
};

interface DesignerTreeProps {
  initialNodes: DesignerNode[];
  onChange?: (nodes: DesignerNode[]) => void;
}

interface TreeItemData extends TreeItem {
  index: TreeItemIndex;
  isFolder: boolean;
  children: TreeItemIndex[];
  data: DesignerNode;
  isExpanded: boolean;
}

type TreeData = Record<TreeItemIndex, TreeItemData>;

type ContextMenuState = {
  x: number;
  y: number;
  itemId: TreeItemIndex | null;
};
/* ================================
   nodes → TreeData
================================ */

function buildTreeData(nodes: DesignerNode[]): TreeData {
  const tree: TreeData = {};

  tree["root"] = {
    index: "root",
    isFolder: true,
    children: [],
    data: {
      id: "root",
      title: "root",
      type: "main",
      order: 1,
      parentId: null,
    },
    isExpanded: true,
  };

  nodes.forEach((n) => {
    tree[n.id] = {
      index: n.id,
      isFolder:
        n.type === "main" ||
        n.type === "maindiv" ||
        n.type === "div" ||
        n.type === "SnTab",
      children: [],
      data: n,
      isExpanded: true,
    };
  });

  nodes.forEach((n) => {
    const parentKey = n.parentId ?? "root";
    tree[parentKey].children.push(n.id);
  });

  Object.values(tree).forEach((item) => {
    item.children.sort((a, b) => tree[a].data.order - tree[b].data.order);
  });
  return tree;
}

/* ================================
   컴포넌트
================================ */

export type DesignerTreeRef = {
  getFocusedItem: () => TreeItemIndex | null;
  getSelectedItems: () => TreeItemIndex[];
};

const DesignerTree = forwardRef<DesignerTreeRef, DesignerTreeProps>(
  ({ initialNodes, onChange }, ref) => {
    const [nodes, setNodes] = useState<DesignerNode[]>(initialNodes);
    const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([]);

    const treeData = useMemo(() => buildTreeData(nodes), [nodes]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(
      null
    );
    const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
    const [focusedItem, setFocusedItem] = useState<TreeItemIndex | null>(null);

    useEffect(() => {
      const close = () => setContextMenu(null);
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }, []);

    useEffect(() => {
      setNodes(initialNodes);
      const allIds = initialNodes.map((node) => node.id); // 👈 id만 추출
      setExpandedItems(allIds);
    }, [initialNodes]);

    useImperativeHandle(ref, () => ({
      getFocusedItem() {
        return focusedItem;
      },
      getSelectedItems() {
        return selectedItems;
      },
    }));

    /* ================================
     Drag & Drop
  ================================ */

    const onDrop = (
      items: TreeItem<DesignerNode>[],
      target: DraggingPosition
    ) => {
      const movingNode = items[0].data;

      let newParentId: string | null = movingNode.parentId;
      let insertIndex: number | null = null;
      // 1️⃣ item 위에 드롭
      if (target.targetType === "item") {
        const targetNode = treeData[target.targetItem].data;

        if (
          targetNode.type === "main" ||
          targetNode.type === "div" ||
          targetNode.type === "maindiv"
        ) {
          newParentId = targetNode.id;
          insertIndex = null; // 항상 마지막
        }
      }

      // 2️⃣ between-items (빈 div 포함)
      if (target.targetType === "between-items") {
        const parentId =
          target.parentItem === "root" ? null : String(target.parentItem);

        const parentNode = parentId !== null ? treeData[parentId]?.data : null;

        // 부모가 folder면 안으로
        if (
          parentNode &&
          (parentNode.type === "main" ||
            parentNode.type === "div" ||
            parentNode.type === "maindiv")
        ) {
          newParentId = parentNode.id;
          insertIndex = target.childIndex; // 마지막
        }
      }

      moveNode(movingNode.id, newParentId, insertIndex);
    };

    /* ================================
     노드 이동
  ================================ */

    const moveNode = (
      nodeId: string,
      newParentId: string | null,
      insertIndex: number | null
    ) => {
      setNodes((prev) => {
        const moving = prev.find((n) => n.id === nodeId);
        if (!moving) return prev;

        const oldParentId = moving.parentId;
        const oldOrder = moving.order;

        let nodes = [...prev];

        // 1️⃣ 기존 부모 쪽 order 당기기 (자리 비워졌으니까)
        nodes = nodes.map((n) => {
          if (n.parentId === oldParentId && n.order > oldOrder) {
            return { ...n, order: n.order - 1 };
          }
          return n;
        });

        // 2️⃣ 새 부모 쪽 order 밀어내기
        if (insertIndex != null) {
          nodes = nodes.map((n) => {
            if (n.parentId === newParentId && n.order >= insertIndex) {
              return { ...n, order: n.order + 1 };
            }
            return n;
          });
        }
        // 3️⃣ 이동 노드 위치 확정
        nodes = nodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                parentId: newParentId,
                order:
                  insertIndex ??
                  nodes.filter((x) => x.parentId === newParentId).length,
              }
            : n
        );
        onChange?.(nodes);
        return nodes;
      });
    };

    const canDropAt = (
      items: TreeItem<DesignerNode>[],
      target: DraggingPosition
    ) => {
      if (target.targetType === "item") {
        const targetNode = treeData[target.targetItem].data;
        return (
          targetNode.type === "main" ||
          targetNode.type === "div" ||
          targetNode.type === "maindiv"
        );
      }

      if (target.targetType === "between-items") {
        const parentId =
          target.parentItem === "root" ? null : String(target.parentItem);

        if (!parentId) return true;

        const parentNode = treeData[parentId]?.data;
        return (
          parentNode?.type === "main" ||
          parentNode?.type === "div" ||
          parentNode?.type === "maindiv"
        );
      }

      return false;
    };

    const reorderByParent = (nodes: DesignerNode[]): DesignerNode[] => {
      const groups = new Map<string | null, DesignerNode[]>();

      nodes.forEach((n) => {
        if (!groups.has(n.parentId)) groups.set(n.parentId, []);
        groups.get(n.parentId)!.push(n);
      });

      const result: DesignerNode[] = [];

      groups.forEach((siblings) => {
        siblings
          .sort((a, b) => a.order - b.order)
          .forEach((node, index) => {
            result.push({ ...node, order: index });
          });
      });

      return result;
    };

    const getNumber = (title: string) => {
      const match = title.match(/(\d+)$/);
      return match ? Number(match[1]) : null;
    };

    const compressNumbers = (nodes: DesignerNode[]): DesignerNode[] => {
      const groups = new Map<string, DesignerNode[]>();

      // 1. 타입별로 그룹화 (subType 우선)
      nodes.forEach((n) => {
        const key = n.subType ?? n.type;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(n);
      });

      const updated: DesignerNode[] = [];

      groups.forEach((list) => {
        // 2. 기존 번호 순서대로 정렬
        list.sort(
          (a, b) => (getNumber(a.title) ?? 0) - (getNumber(b.title) ?? 0)
        );

        // 3. 1번부터 다시 부여 (중간 빈 번호만 당김)
        list.forEach((node, index) => {
          const baseTitle = node.title.replace(/\d+$/, "");
          const newTitle = `${baseTitle}${index + 1}`;

          updated.push({
            ...node,
            title: newTitle,
            subTitle: node.subType ? newTitle : node.subTitle,
          });
        });
      });

      return updated;
    };

    const deleteNode = (id: string) => {
      setNodes((prev) => {
        const target = prev.find((n) => n.id === id);
        if (!target) return prev;

        if (
          !window.confirm(
            `"${target.title}" 을(를) 삭제하시겠습니까?\n하위 항목도 함께 삭제됩니다.`
          )
        )
          return prev;

        // 1️⃣ 삭제 대상 수집
        const removeIds = new Set<string>();
        const collect = (targetId: string) => {
          removeIds.add(targetId);
          prev.forEach((n) => {
            if (n.parentId === targetId) collect(n.id);
          });
        };
        collect(id);

        const remaining = prev.filter((n) => !removeIds.has(n.id));

        // 2️⃣ order 재정렬 (부모 기준)
        const reordered = reorderByParent(remaining);

        // 3️⃣ 번호 압축 (⭐ 여기 핵심)
        const finalNodes = compressNumbers(reordered);

        onChange?.(finalNodes);
        return finalNodes;
      });
    };

    const addTabPage = (id: string) => {
      setNodes((prev) => {
        // 1️⃣ 마지막 id + 1
        const maxId = Math.max(
          ...prev.map((n) => Number(n.id)).filter((n) => !isNaN(n)),
          0
        );
        const newId = String(maxId + 1);

        const order = prev.filter((n) => n.parentId === id).length;
        const GridCount = prev.filter(
          (n) => n.type === "SnGrid" || n.subType === "SnGrid"
        ).length;
        const tabpage: DesignerNode = {
          id: newId,
          type: "TabPage", // NodeType 쓰면 NodeType으로
          parentId: id,
          order: order,
          title: `그리드${GridCount + 1}`,
          subType: "SnGrid",
          subTitle: `그리드${GridCount + 1}`,
        };
        const update = [...prev, tabpage];
        onChange?.(update);
        return update;
      });
    };

    const setLayout = (id: string, layout: "row" | "column") => {
      setNodes((prev) => {
        const updated = prev.map((n) => (n.id === id ? { ...n, layout } : n));

        onChange?.(updated);
        return updated;
      });
    };

    function flattenByVisualOrder(nodes: DesignerNode[]) {
      const map = new Map<string | null, DesignerNode[]>();

      nodes.forEach((n) => {
        if (!map.has(n.parentId)) map.set(n.parentId, []);
        map.get(n.parentId)!.push(n);
      });

      map.forEach((arr) => arr.sort((a, b) => a.order - b.order));

      const result: DesignerNode[] = [];

      function walk(parentId: string | null) {
        const children = map.get(parentId) || [];
        for (const child of children) {
          result.push(child);
          walk(child.id);
        }
      }

      walk(null);
      return result;
    }

    const setSubType = (id: string, subType: "SnGrid" | "SnForm") => {
      setNodes((prev) => {
        // 1️⃣ 타입 변경
        const changed = prev.map((n) => (n.id === id ? { ...n, subType } : n));

        // 2️⃣ 화면 순서대로 정렬된 리스트 만들기
        const visualOrder = flattenByVisualOrder(changed);

        // 3️⃣ 타입별 번호 다시 매기기 (🔥 핵심)
        const typeCount = new Map<string, number>();

        const renumbered = visualOrder.map((n) => {
          const displayType = n.subType ?? n.type;

          if (displayType === "SnGrid" || displayType === "SnForm") {
            const count = (typeCount.get(displayType) ?? 0) + 1;
            typeCount.set(displayType, count);

            const newTitle =
              displayType === "SnGrid" ? `그리드${count}` : `프리폼${count}`;

            return { ...n, title: newTitle, subTitle: newTitle };
          }

          return n;
        });

        onChange?.(renumbered);
        return renumbered;
      });
    };

    /* ================================
     Render
  ================================ */

    return (
      <div style={{ height: 400 }}>
        <ControlledTreeEnvironment<DesignerNode>
          items={treeData}
          getItemTitle={(item) => item.data.title}
          onSelectItems={(items) => setSelectedItems(items)}
          onFocusItem={(item) => {
            setFocusedItem(item.index);
          }}
          canDragAndDrop
          canReorderItems
          canDropOnFolder={true}
          canDropOnNonFolder={false}
          canDropAt={canDropAt}
          viewState={{
            designer: { expandedItems },
          }}
          onExpandItem={(item) =>
            setExpandedItems((prev) => [...prev, item.index])
          }
          onCollapseItem={(item) =>
            setExpandedItems((prev) => prev.filter((id) => id !== item.index))
          }
          onDrop={onDrop}
          defaultInteractionMode={InteractionMode.ClickArrowToExpand}
        >
          <div
            onContextMenu={(e) => {
              e.preventDefault();
              if (!selectedItems) return;
              let targetId = selectedItems[0];

              // 우선순위 2: 포커스된 것
              if (!targetId && focusedItem) {
                targetId = focusedItem;
                setSelectedItems([focusedItem]); // 🔥 중요
              }

              if (!targetId) return;
              setContextMenu({
                x: e.clientX,
                y: e.clientY,
                itemId: targetId,
              });
            }}
          >
            <Tree treeId="designer" rootItem="root" />
          </div>
        </ControlledTreeEnvironment>
        {contextMenu && contextMenu.itemId && treeData[contextMenu.itemId] && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            node={treeData[contextMenu.itemId].data}
            onDelete={() => {
              deleteNode(contextMenu.itemId as string);
              setContextMenu(null);
            }}
            onSetLayout={(layout) => {
              setLayout(contextMenu.itemId as string, layout);
              setContextMenu(null);
            }}
            onDeleteTabpage={() => {
              deleteNode(contextMenu.itemId as string);
              setContextMenu(null);
            }}
            onAddTabpage={() => {
              addTabPage(contextMenu.itemId as string);
              setContextMenu(null);
            }}
            onSetTabpage={(subType) => {
              setSubType(contextMenu.itemId as string, subType);
              setContextMenu(null);
            }}
          />
        )}
      </div>
    );
  }
);

export default DesignerTree;

function ContextMenu({
  x,
  y,
  node,
  onDelete,
  onSetLayout,
  onDeleteTabpage,
  onAddTabpage,
  onSetTabpage,
}: {
  x: number;
  y: number;
  node: DesignerNode;
  onDelete: () => void;
  onSetLayout: (layout: "row" | "column") => void;
  onDeleteTabpage: () => void;
  onAddTabpage: () => void;
  onSetTabpage: (layout: "SnGrid" | "SnForm") => void;
}) {
  const isLayoutNode =
    node.type === "main" || node.type === "maindiv" || node.type === "div";

  const isTab = node.type === "SnTab";
  const isTabPage = node.type === "TabPage";

  return (
    <div
      style={{
        position: "fixed",
        top: y,
        left: x,
        background: "#2b2b2b",
        color: "#fff",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
        zIndex: 9999,
        minWidth: 120,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <MenuItem onClick={onDelete}>삭제</MenuItem>

      {isLayoutNode && (
        <>
          <MenuItem onClick={() => onSetLayout("column")}>가로</MenuItem>
          <MenuItem onClick={() => onSetLayout("row")}>세로</MenuItem>
        </>
      )}
      {isTab && (
        <>
          <MenuItem onClick={() => onAddTabpage()}>탭페이지추가</MenuItem>
        </>
      )}

      {isTabPage && (
        <>
          <MenuItem onClick={() => onSetTabpage("SnGrid")}>그리드</MenuItem>
          <MenuItem onClick={() => onSetTabpage("SnForm")}>프리폼</MenuItem>
        </>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "6px 10px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
}
